import React, { useEffect, useState } from "react";
import { listWorkbookSheets } from "../excel/exportBoqPdf";
import { openSheetsInNewWindow } from "../excel/openSheetsInNewWindow";

export default function OpenSheetsPage({ onBack }) {
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [allSheets, setAllSheets] = useState([]);
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState("");
  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);

  useEffect(() => {
    if (!titleTouched) setTitle(selectedSheets[0] || "");
  }, [selectedSheets, titleTouched]);

  const loadSheets = () => {
    setStatus("loading");
    setMessage("");
    setTitleTouched(false);
    listWorkbookSheets()
      .then(({ activeName, allNames, recommendedNames }) => {
        setActiveSheet(activeName || "");
        setAllSheets(allNames || []);
        setSelectedSheets(
          recommendedNames && recommendedNames.length ? recommendedNames : allNames || []
        );
        setStatus("ready");
      })
      .catch((err) => {
        setMessage(err?.message || "ფურცლების ჩატვირთვა ვერ მოხერხდა");
        setStatus("error");
      });
  };

  const runOpen = () => {
    if (!selectedSheets.length) {
      setStatus("error");
      setMessage("აირჩიე მინიმუმ ერთი ფურცელი.");
      return;
    }
    setStatus("running");
    setMessage("");
    openSheetsInNewWindow(selectedSheets, title || selectedSheets[0] || "")
      .then(() => setStatus("done"))
      .catch((err) => {
        setMessage(err?.message || "უცნობი შეცდომა");
        setStatus("error");
      });
  };

  useEffect(() => { loadSheets(); }, []);

  const toggleSheet = (name) =>
    setSelectedSheets((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );

  const btnBase = {
    borderRadius: 6, padding: "8px 12px", fontSize: 12, cursor: "pointer",
    border: "1px solid rgba(148,163,184,0.25)",
    background: "var(--surface-soft)", color: "var(--text-muted)",
    flex: 1,
  };

  return (
    <div style={{
      minHeight: "100vh", background: "var(--page-gradient)",
      padding: "20px 16px", color: "var(--text-primary)",
      fontFamily: "Arial, sans-serif",
    }}>
      <button className="link" onClick={onBack}>← უკან</button>

      <h2 style={{ marginTop: 16, marginBottom: 4 }}>ახალ სარკმელში გახსნა</h2>
      <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.5 }}>
        შეარჩიე ფურცლები, რომლებიც ახალ სამუშაო წიგნში გახსნა გინდა.
      </p>

      {/* ── Loading ── */}
      {status === "loading" && (
        <div style={{
          background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.15)",
          borderRadius: 10, padding: "32px 0", textAlign: "center",
          fontSize: 13, color: "var(--text-muted)",
        }}>
          BOQ ფურცლების კითხვა...
        </div>
      )}

      {/* ── Running ── */}
      {status === "running" && (
        <div style={{
          background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.15)",
          borderRadius: 10, padding: "32px 0", textAlign: "center",
          fontSize: 13, color: "var(--text-muted)",
        }}>
          სამუშაო წიგნი მზადდება...
        </div>
      )}

      {/* ── Ready ── */}
      {status === "ready" && (
        <div>
          {/* Title input */}
          <div style={{ marginBottom: 12 }}>
            <label style={{
              display: "block", fontSize: 11, fontWeight: 700, color: "var(--text-muted)",
              textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5,
            }}>
              სამუშაო წიგნის სათაური
            </label>
            <input
              type="text"
              value={title}
              placeholder={selectedSheets[0] || "სათაური..."}
              onChange={(e) => {
                setTitleTouched(!!e.target.value);
                setTitle(e.target.value);
              }}
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "8px 12px", borderRadius: 6, fontSize: 12,
                border: "1px solid rgba(148,163,184,0.3)",
                background: "var(--surface-soft)", color: "var(--text-primary)",
                outline: "none",
              }}
            />
          </div>

          {/* Active sheet info */}
          <div style={{
            background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.15)",
            borderRadius: 8, padding: "10px 12px", marginBottom: 10,
            fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5,
          }}>
            აქტიური ფურცელი: <strong style={{ color: "var(--text-primary)" }}>{activeSheet || "—"}</strong>
            <br />
            მონიშნულია აქტიური და child ფურცლები. შეგიძლია შეცვალო მონიშვნა.
          </div>

          {/* Sheet list */}
          <div style={{
            border: "1px solid rgba(148,163,184,0.2)",
            borderRadius: 8, maxHeight: 240, overflowY: "auto", marginBottom: 10,
            background: "var(--surface-soft)",
          }}>
            {allSheets.map((name) => {
              const checked = selectedSheets.includes(name);
              return (
                <label
                  key={name}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 12px",
                    borderBottom: "1px solid rgba(148,163,184,0.08)",
                    cursor: "pointer", fontSize: 12,
                    background: checked ? "rgba(6,182,212,0.06)" : "transparent",
                    color: checked ? "var(--text-primary)" : "var(--text-muted)",
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleSheet(name)} />
                  <span style={{ flex: 1 }}>{name}</span>
                </label>
              );
            })}
          </div>

          {/* Select all / none */}
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button onClick={() => setSelectedSheets(allSheets)} style={btnBase}>
              ყველა
            </button>
            <button onClick={() => setSelectedSheets([])} style={btnBase}>
              მოხსნა
            </button>
          </div>

          {/* Action */}
          <button
            onClick={runOpen}
            style={{
              width: "100%", padding: "10px", borderRadius: 8,
              border: "none", background: "var(--accent-primary)", color: "#fff",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            გახსნა ({selectedSheets.length})
          </button>
        </div>
      )}

      {/* ── Done ── */}
      {status === "done" && (
        <div>
          <div style={{
            background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.3)",
            borderRadius: 10, padding: "14px 16px", marginBottom: 14,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>✓ ფაილი ჩამოიტვირთა</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              გახსენი ჩამოტვირთული ფაილი Excel-ში.
            </div>
          </div>
          <button
            onClick={loadSheets}
            style={{
              width: "100%", padding: "9px", borderRadius: 8,
              border: "none", background: "var(--accent-primary)", color: "#fff",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            ახლიდან არჩევა
          </button>
        </div>
      )}

      {/* ── Error ── */}
      {status === "error" && (
        <div>
          <div style={{
            background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 10, padding: "12px 14px", marginBottom: 14, color: "#fca5a5",
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>შეცდომა</div>
            <div style={{ fontSize: 12 }}>{message}</div>
          </div>
          <button
            onClick={loadSheets}
            style={{
              width: "100%", padding: "9px", borderRadius: 8,
              border: "none", background: "var(--accent-primary)", color: "#fff",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            კვლავ სცადე
          </button>
        </div>
      )}
    </div>
  );
}
